<template>
  <v-container>
    <v-card>
      <v-card-title>
        <h1 class="text-h3 mb-4 text-center">DEXlib Licenses</h1>
        <v-spacer></v-spacer>
        <v-btn color="primary" prepend-icon="mdi-account-plus" :to="{ name: 'dexlib-license-add' }">
          Add License
        </v-btn>
      </v-card-title>

      <v-data-table :headers="headers" :items="licenses" :loading="loading" class="elevation-1">
        <template v-slot:progress>
          <v-progress-linear color="primary" indeterminate></v-progress-linear>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="isAdmin"
            icon="mdi-delete"
            color="error"
            size="small"
            variant="text"
            @click="confirmDelete(item)"
          >
          </v-btn>
          <v-btn
            icon="mdi-plus"
            color="primary"
            size="small"
            variant="text"
            :to="{
              name: 'dexlib-license-add',
              query: {
                practice: item.practice,
              },
            }"
          >
            <v-icon>mdi-plus</v-icon>
            <v-tooltip activator="parent" location="top">
              Create new license with this practice info
            </v-tooltip>
          </v-btn>
        </template>
        <template v-slot:item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
      </v-data-table>
    </v-card>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6"> Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete DEXlib license {{ selectedLicense?.id }}?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="deleteDialog = false"> Cancel</v-btn>
          <v-btn color="error" variant="tonal" @click="deleteLicense" :loading="deleteLoading">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import axios from 'axios';
import { useUserStore } from '@/stores/user-store.js';

const userStore = useUserStore();

interface License {
  id: number;
  practice: string;
  sensorSerialNum: string;
  licenseKey: string;
  createdAt: string;
  updatedAt: string;
  comment: string;
  userEmail: string;
}

const licenses = ref<License[]>([]);
const loading = ref(true);

const deleteDialog = ref(false);
const deleteLoading = ref(false);
const selectedLicense = ref<License | null>(null);

const isAdmin = computed(() => {
  return userStore.user?.role === 'ADMIN';
});

const headers = computed(() => {
  const baseHeaders = [
    { title: 'Practice', key: 'practice' },
    { title: 'Sensor Serial Number', key: 'sensorSerialNum' },
    { title: 'License Key', key: 'licenseKey' },
    { title: 'Created At', key: 'createdAt' },
    { title: 'Comment', key: 'comment' },
  ];

  if (isAdmin.value) {
    baseHeaders.push({ title: 'Created By', key: 'userEmail' });
  }

  baseHeaders.push({ title: 'Actions', key: 'actions', sortable: false, align: 'end' });

  return baseHeaders;
});

const confirmDelete = (item: License) => {
  selectedLicense.value = item;
  deleteDialog.value = true;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString();
};

const deleteLicense = async () => {
  if (!selectedLicense.value) return;

  try {
    deleteLoading.value = true;
    await axios.delete(`/api/dexlib-licenses/${selectedLicense.value.id}`);
    await fetchLicenses(); // Refresh the list
    deleteDialog.value = false;
  } catch (error) {
    console.error('Error deleting DEXlib license:', error);
    // You might want to add error handling/notification here
  } finally {
    deleteLoading.value = false;
  }
};

const fetchLicenses = async () => {
  try {
    loading.value = true;
    const response = await axios.get('/api/dexlib-licenses');
    licenses.value = response.data;
  } catch (error) {
    console.error('Error fetching licenses:', error);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchLicenses();
});
</script>

<style scoped>
.v-data-table {
  width: 100%;
}
</style>
